import PageShell from "../components/PageShell";
import {
  UserCog,
  CreditCard,
  Bug,
  Lightbulb,
  Trash2,
  Mail,
  ArrowRight,
} from "lucide-react";

const CATEGORIES = [
  {
    icon: UserCog,
    title: "Account Help",
    desc: "Login issues, username changes, profile updates, or account recovery.",
  },
  {
    icon: CreditCard,
    title: "Subscription Help",
    desc: "Billing questions, plan changes, restoring purchases, or refund inquiries via Apple.",
  },
  {
    icon: Bug,
    title: "Bug Reports",
    desc: "Something broken, crashing, or behaving unexpectedly? Send the details and we'll dig in.",
  },
  {
    icon: Lightbulb,
    title: "Feature Requests",
    desc: "Ideas to make IRL Stats better. We read every suggestion.",
  },
  {
    icon: Trash2,
    title: "Data Deletion Requests",
    desc: "Request deletion of your account and all associated data. Just email us and we'll handle it.",
  },
];

const Support = () => {
  const mailtoBase = "mailto:irlstatsapp@gmail.com";

  return (
    <PageShell testId="support-page">
      <section className="relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 py-16 md:py-24">
          <p className="text-amber-300/80 text-xs tracking-[0.25em] uppercase mb-4">
            Support · IRL Stats
          </p>
          <h1
            data-testid="support-heading"
            className="font-cinzel text-4xl sm:text-5xl lg:text-6xl text-amber-50 leading-tight"
          >
            Need help with{" "}
            <span className="text-amber-300 italic">IRL Stats?</span>
          </h1>
          <p className="text-stone-300 text-base sm:text-lg mt-5 max-w-2xl leading-relaxed">
            We're a small team building IRL Stats with care. Tell us what's
            going on and we'll get back to you as soon as possible.
          </p>

          <div className="mt-10 p-6 sm:p-7 rounded-xl border border-amber-500/25 bg-gradient-to-b from-stone-900/60 to-black">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-5">
              <div>
                <p className="text-amber-200 text-xs tracking-[0.2em] uppercase mb-2">
                  Contact Email
                </p>
                <a
                  href={mailtoBase}
                  data-testid="support-email-link"
                  className="font-cinzel text-amber-100 text-2xl sm:text-3xl hover:text-amber-300 transition-colors break-all"
                >
                  irlstatsapp@gmail.com
                </a>
                <p className="text-stone-400 text-sm mt-3">
                  We typically respond as soon as possible.
                </p>
              </div>
              <a
                href={mailtoBase}
                data-testid="support-email-button"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-md bg-gradient-to-b from-amber-300 to-amber-500 text-black font-semibold tracking-wide whitespace-nowrap shadow-[0_0_25px_rgba(251,191,36,0.2)] hover:shadow-[0_0_40px_rgba(251,191,36,0.4)] transition-shadow"
              >
                <Mail size={16} />
                Email Support
              </a>
            </div>
          </div>

          <div className="mt-16">
            <p className="text-amber-300/80 text-xs tracking-[0.25em] uppercase mb-4">
              ── How can we help?
            </p>
            <h2 className="font-cinzel text-2xl sm:text-3xl text-amber-50 leading-tight mb-8">
              Pick a category to get started.
            </h2>

            <div className="grid sm:grid-cols-2 gap-4">
              {CATEGORIES.map((c, idx) => (
                <a
                  key={c.title}
                  href={`${mailtoBase}?subject=${encodeURIComponent(
                    `[${c.title}] — `
                  )}`}
                  data-testid={`support-category-${idx}`}
                  className="group flex gap-4 rounded-xl border border-amber-500/15 bg-stone-950/60 p-5 hover:border-amber-400/50 hover:bg-stone-900/60 transition-all"
                >
                  <div className="shrink-0 h-11 w-11 rounded-lg border border-amber-400/30 bg-amber-400/5 flex items-center justify-center group-hover:border-amber-300/70 group-hover:bg-amber-400/10 transition-colors">
                    <c.icon className="text-amber-300" size={20} />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <h3 className="font-cinzel text-amber-100 text-base tracking-wide">
                        {c.title}
                      </h3>
                      <ArrowRight
                        className="text-amber-400/40 group-hover:text-amber-300 transition-colors shrink-0"
                        size={16}
                      />
                    </div>
                    <p className="text-stone-400 text-sm leading-relaxed mt-1.5">
                      {c.desc}
                    </p>
                  </div>
                </a>
              ))}
            </div>
          </div>

          <div className="mt-14 p-5 rounded-lg border border-amber-500/15 bg-amber-400/5 text-stone-400 text-sm">
            <p>
              <span className="text-amber-200 font-medium">Heads up:</span>{" "}
              When emailing about a bug or subscription issue, include your
              username, device model, iOS version, and a short description of
              what happened. That helps us solve things faster.
            </p>
          </div>

          <div className="mt-10 text-stone-500 text-xs">
            <p>The Legacy Labs LLC, dba IRL Stats</p>
          </div>
        </div>
      </section>
    </PageShell>
  );
};

export default Support;
